<div class="survey-overview">
  <div class="loading-state" *ngIf="isLoading">
    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
    <span translate>WORK_ENVIRONMENT_SURVEY.LOADING</span>
  </div>

  <div class="empty-state" *ngIf="!isLoading && !surveyId">
    <p class="empty-state-title" translate>
      WORK_ENVIRONMENT_SURVEY.NO_SURVEYS_TITLE
    </p>
    <p class="empty-state-body" translate>
      WORK_ENVIRONMENT_SURVEY.NO_SURVEYS_BODY
    </p>
  </div>

  <div class="overview-grid" *ngIf="!isLoading && hasData">
    <!-- Overall score -->
    <div class="card overview-card overview-card--narrow">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.OVERVIEW.TOTAL</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="'WORK_ENVIRONMENT_SURVEY.OVERVIEW.TOTAL_HINT' | translate"
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <survey-score-gauge
        [value]="surveyAverage?.AverageScore ?? null"
        [max]="5"
        [caption]="
          insights
            ? ('WORK_ENVIRONMENT_SURVEY.INDEX' | translate) +
              ' ' +
              insights.BenchmarkIndex
            : ''
        "
      ></survey-score-gauge>
    </div>

    <!-- Department comparison -->
    <div class="card overview-card overview-card--wide">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.DEPARTMENT</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="
            'WORK_ENVIRONMENT_SURVEY.OVERVIEW.DEPARTMENT_HINT' | translate
          "
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <div class="card-chart" *ngIf="departmentChartData; else noChartData">
        <p-chart
          type="bar"
          [data]="departmentChartData"
          [options]="departmentChartOptions"
          height="200px"
        ></p-chart>
      </div>
    </div>

    <!-- History across surveys -->
    <div class="card overview-card overview-card--wide">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.HISTORY</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="
            'WORK_ENVIRONMENT_SURVEY.OVERVIEW.HISTORY_HINT' | translate
          "
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <div class="card-chart" *ngIf="historyChartData; else noChartData">
        <p-chart
          type="line"
          [data]="historyChartData"
          [options]="historyChartOptions"
          height="200px"
        ></p-chart>
      </div>
    </div>

    <!-- Honesty. Placeholder surface — nothing computes this yet. -->
    <div class="card overview-card overview-card--narrow">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.OVERVIEW.HONESTY</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="
            'WORK_ENVIRONMENT_SURVEY.OVERVIEW.HONESTY_HINT' | translate
          "
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <survey-score-gauge
        [value]="insights?.HonestyScore ?? null"
        [max]="5"
      ></survey-score-gauge>
    </div>

    <!-- Response rate -->
    <div class="card overview-card overview-card--narrow">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.OVERVIEW.RESPONSE_RATE</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="
            'WORK_ENVIRONMENT_SURVEY.OVERVIEW.RESPONSE_RATE_HINT' | translate
          "
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <survey-score-gauge
        arc="full"
        icon="fa-user"
        unit="%"
        [decimals]="0"
        [max]="100"
        [value]="responseRate?.ResponseRatePercent ?? null"
        [caption]="'WORK_ENVIRONMENT_SURVEY.OVERVIEW.COMPLETED' | translate"
      ></survey-score-gauge>
      <div class="card-footnote" *ngIf="responseRate">
        {{ responseRate.RespondedCount }} / {{ responseRate.ParticipantCount }}
        {{ "WORK_ENVIRONMENT_SURVEY.PARTICIPANTS" | translate }}
      </div>
    </div>

    <!-- Best category -->
    <div class="card overview-card overview-card--narrow">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.OVERVIEW.BEST_CATEGORY</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="
            'WORK_ENVIRONMENT_SURVEY.OVERVIEW.BEST_CATEGORY_HINT' | translate
          "
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <survey-score-gauge
        arc="full"
        [max]="5"
        [value]="bestWorst?.BestCategory?.AverageScore ?? null"
      ></survey-score-gauge>
      <div class="card-footnote">
        {{ bestWorst?.BestCategory?.CategoryName || "—" }}
      </div>
    </div>

    <!-- Weakest category -->
    <div class="card overview-card overview-card--narrow">
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.OVERVIEW.IMPROVEMENT_AREA</span>
        <i
          class="fa fa-info-circle card-heading__info"
          [pTooltip]="
            'WORK_ENVIRONMENT_SURVEY.OVERVIEW.IMPROVEMENT_AREA_HINT' | translate
          "
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </div>
      <survey-score-gauge
        arc="full"
        [max]="5"
        [value]="bestWorst?.WorstCategory?.AverageScore ?? null"
      ></survey-score-gauge>
      <div class="card-footnote">
        {{ bestWorst?.WorstCategory?.CategoryName || "—" }}
      </div>
    </div>

    <!-- Insight. Placeholder surface — nothing computes this yet. -->
    <div
      class="card overview-card overview-card--full overview-card--insight"
      *ngIf="insights && insights.Suggestions.length"
    >
      <div class="card-heading">
        <span translate>WORK_ENVIRONMENT_SURVEY.OVERVIEW.INSIGHT</span>
      </div>
      <div class="insight-list">
        <div
          class="insight-item"
          *ngFor="let suggestion of insights.Suggestions"
        >
          <div class="insight-item__label" translate>
            WORK_ENVIRONMENT_SURVEY.OVERVIEW.SUGGESTED_ACTION
          </div>
          <p class="insight-item__body">{{ suggestion }}</p>
        </div>
      </div>
    </div>
  </div>

  <ng-template #noChartData>
    <div class="card-chart card-chart--empty">
      <span translate>WORK_ENVIRONMENT_SURVEY.NO_RESULTS</span>
    </div>
  </ng-template>
</div>
